import React, { useState } from "react";

import { Button, Dialog, useToasts } from "@puzzle/ui";
import { formatMoney, parseDate } from "@puzzle/utils";

import Analytics from "lib/analytics/analytics";
import { useActiveCompany } from "components/companies/ActiveCompanyProvider";
import { useCompanyDateFormatter } from "components/companies/useCompanyDateFormatter";

import {
  PayrollItemFragment,
  PayrollsDocument,
  useDeletePayrollMutation,
} from "./graphql.generated";
import { calculateTotal, usePayrollPage } from "./PayrollProvider";

export const DeletePayrollModal = ({
  activePayroll,
  open: _open,
  onOpenChange: _onOpenChange,
  ...props
}: React.ComponentProps<typeof Dialog> & {
  activePayroll: PayrollItemFragment;
}) => {
  const [deletePayroll, { loading }] = useDeletePayrollMutation({
    refetchQueries: [PayrollsDocument],
  });
  const { setActivePayroll, pagedPayrolls, activePayrollIndex } = usePayrollPage();
  const { company } = useActiveCompany<true>();
  const { toast } = useToasts();
  const [internalOpen, setInternalOpen] = useState(_open);
  const open = _open ?? internalOpen;
  const onOpenChange = _onOpenChange ?? setInternalOpen;
  const formatter = useCompanyDateFormatter({
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });

  return (
    <Dialog
      {...props}
      size="xsmall"
      open={open}
      onOpenChange={(open) => {
        onOpenChange(open);
      }}
    >
      <Dialog.Title>Are you sure?</Dialog.Title>
      <Dialog.Body>
        <div>Removing this payroll will also remove all the associated journal entries.</div>
        <div>This action cannot be undone.</div>
      </Dialog.Body>
      <Dialog.Footer align="right">
        <Dialog.Actions>
          <Dialog.Close asChild>
            <Button variant="minimal">Cancel</Button>
          </Dialog.Close>
          <Button
            variant="negative"
            loading={loading}
            onClick={async () => {
              await deletePayroll({
                variables: {
                  input: {
                    companyId: company.id,
                    payrollId: activePayroll.id,
                  },
                },

                onCompleted: () => {
                  Analytics.payrollDeleted({ id: activePayroll.id });

                  toast({
                    title: `Payroll deleted`,
                    message: `Payroll amount: ${formatMoney({
                      currency: "USD",
                      amount: calculateTotal(activePayroll),
                    })}. Posted on: ${formatter.format(parseDate(activePayroll.checkDate))}`,
                    status: "success",
                  });

                  onOpenChange(false);

                  if (pagedPayrolls.length === 1) {
                    setActivePayroll(undefined);
                    return;
                  }

                  const prevPayroll = pagedPayrolls[activePayrollIndex - 1];
                  setActivePayroll(prevPayroll ? prevPayroll.id : pagedPayrolls[1].id);
                },

                onError: () => {
                  Analytics.payrollDeleteFailed({ id: activePayroll.id });

                  toast({
                    title: `Payroll delete failed. Please try again.`,
                    status: "error",
                  });
                },
              });
            }}
          >
            Delete payroll
          </Button>
        </Dialog.Actions>
      </Dialog.Footer>
    </Dialog>
  );
};
